import { cn } from "@/lib/utils";
import {
  segmentClasses,
  type CustomerSegment,
} from "../industryTheme/industryTheme.util";

export interface CustomerSegmentCard {
  segment: CustomerSegment;
  // Chip text, e.g. "New", "At risk".
  label: string;
  // One character in the round avatar, e.g. "N", "!".
  avatar: string;
  text: string;
}

interface CustomerSegmentCardsProps {
  cards: CustomerSegmentCard[];
  note?: string;
}

// "Know which customers are slipping" - the preview's `.sc-grid`. One card
// per customer group (new, repeat, VIP, at risk, lost) in a left-to-right
// path, each with the group's colour on its top rule, avatar and chip, and
// what to send that group.
const CustomerSegmentCards = ({ cards, note }: CustomerSegmentCardsProps) => (
  <div>
    <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5 xl:gap-[22px]">
      {cards.map((card, index) => {
        const segment = segmentClasses[card.segment];
        return (
          <li
            key={card.label}
            className={cn(
              "relative grid content-start gap-3 rounded-[26px] border-t-[6px] px-[18px] pt-[22px] pb-5 shadow-card transition-transform duration-300 hover:-translate-y-1.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
              segment.card,
              segment.border,
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "grid size-11 place-items-center rounded-full text-[1.1rem] font-extrabold text-white",
                segment.solid,
              )}
            >
              {card.avatar}
            </span>
            <span
              className={cn(
                "justify-self-start rounded-full px-4 py-[5px] text-[14px] font-extrabold",
                segment.chip,
              )}
            >
              {card.label}
            </span>
            <p className="text-[14.5px] leading-snug text-ink">{card.text}</p>
            {/* Chevron to the next group, only when all five sit in a row. */}
            {index < cards.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute top-[42px] -right-[17px] hidden size-3 rotate-45 border-t-[3px] border-r-[3px] border-line xl:block"
              />
            )}
          </li>
        );
      })}
    </ul>
    {note && (
      <p className="mt-4 text-[14px] leading-relaxed text-ink-muted">{note}</p>
    )}
  </div>
);

export default CustomerSegmentCards;
